import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { FaaReportOptions } from '../../../core/data-models/faa-report-options.model';
import { StateReportOptions } from '../../../core/data-models/state-report-options.model';
import { CertificationService } from '../../../core/data-services/certification.service';
import { OrganizationContextService } from '../../../core/services/organization-context.service';
import { OrganizationService } from '../../../core/data-services/organization.service';
import { ToastService } from '../../../core/services/toast.service';

import {
  ReportAgency,
  isAgencyEmailPending,
  listReportAgencies,
  preselectReportAgency
} from '../../constants/report-agencies';
import { copyToClipboard } from '../../utils/clipboard';
import { buildMailtoUrl, isWithinMailtoLimit } from '../../utils/mailto';
import {
  FAA_CERTIFICATE_PLACEHOLDER,
  STATE_CERTIFICATE_PLACEHOLDER,
  resolveReportCertificateNumber
} from '../../utils/report-certificates';
import {
  STATE_REPORT_REVIEW_NOTE,
  buildReportEmailTemplate
} from '../../utils/report-email-template';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

/**
 * Which compliance report the modal is composing a submission for.
 */
export type EmailReportKind = 'faa' | 'state';

/**
 * The subset of FaaReport / StateReport the modal needs.
 *
 * Both models are structurally compatible with this shape, so either can be
 * assigned to the `report` input without a cast.
 */
export interface EmailReportModalReport {
  id: string;
  name?: string;
  reportType?: string;
  state?: string; // State reports only
  startDate: string;
  endDate: string;
  status?: string;
  /**
   * Drives the draft's bullet list. `FaaReportOptions` and `StateReportOptions` are entirely
   * different shapes; the builder picks the right map from `reportKind`.
   */
  options?: FaaReportOptions | StateReportOptions;
}

/** Sentinel for the "Other recipient…" row — never a real agency id. */
const OTHER_RECIPIENT = '__other__';

/** Focusable descendants considered by the dialog's Tab trap. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Email Report Modal
 *
 * ADN **composes** a correctly addressed, correctly worded compliance submission and shows it to
 * the user. The user **sends** it from their own mail client — nothing here sends mail, and the
 * copy never claims otherwise.
 *
 * Three steps, deliberately ordered:
 *   1. Recipient — agency preset or free-text, address separately copyable.
 *   2. Download the PDF — `mailto:` cannot attach files (no attachment parameter exists in any
 *      browser, on any OS), so the user attaches it themselves and the file has to be on disk
 *      before the mail client grabs focus.
 *   3. The editable draft — Copy Message (primary, always works) beside Open in Email App
 *      (secondary, allowed to silently fail).
 *
 * The host page owns which report is open (`isOpen` / `report`), performs the PDF download
 * (`downloadRequested`) and the submit call (`submitted`), because both APIs live on a
 * report-kind-specific service. It drives the modal's async feedback through `setProcessing()` /
 * `setError()` — the same pattern ConfirmModalComponent uses.
 *
 * Reference: /agent-os/specs/2026-08-03-report-agency-email-stopgap/spec.md § 2 – § 4.7, § 6
 */
@Component({
  selector: 'app-email-report-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MaxLengthDirective, CharCounterDirective],
  templateUrl: './email-report-modal.component.html',
  styleUrls: ['./email-report-modal.component.scss']
})
export class EmailReportModalComponent implements OnChanges {
  private certificationService = inject(CertificationService);
  private organizationService = inject(OrganizationService);
  private organizationContext = inject(OrganizationContextService);
  private toast = inject(ToastService);

  @Input() reportKind: EmailReportKind = 'faa';
  @Input() report: EmailReportModalReport | null = null;
  @Input() isOpen: boolean = false;

  /**
   * Pre-formatted display strings supplied by the host page.
   * The report services own name/state/date-range formatting, so the modal
   * takes the rendered strings rather than injecting either service.
   */
  @Input() reportTitle: string = '';
  @Input() reportSubtitle: string = '';

  /** Emits the recipient address when the user marks the report as submitted. */
  @Output() submitted = new EventEmitter<string>();
  /** Emits when the user dismisses the modal (Cancel, X, backdrop, Escape). */
  @Output() closed = new EventEmitter<void>();
  /**
   * Emits when the user asks for the PDF. The host runs its existing
   * fresh-presigned-URL download flow unchanged.
   */
  @Output() downloadRequested = new EventEmitter<void>();

  @ViewChild('dialogRoot') dialogRoot?: ElementRef<HTMLElement>;

  // --------------------------------------------
  // Template-driven form fields
  // --------------------------------------------

  /** Free-text recipient, used for "Other recipient…" and for presets with a pending address. */
  customEmail: string = '';
  /** Editable draft. `subject` / `body` are the ONLY source Copy and mailto ever read. */
  subject: string = '';
  body: string = '';

  // --------------------------------------------
  // UI state
  // --------------------------------------------

  isProcessing = signal(false);
  errorMessage = signal<string | null>(null);

  /** Selected agency id, or {@link OTHER_RECIPIENT}. */
  recipientChoice = signal<string>(OTHER_RECIPIENT);
  /** Presets offered for this report — see {@link resolveAgencyOptions}. */
  agencyOptions = signal<ReportAgency[]>([]);

  /**
   * ⚠️ The dirty flag. Once the user touches the subject or the body we never regenerate over
   * their typing. Silently discarding someone's edits because they switched a radio button is a
   * bad bug and a cheap one to prevent.
   */
  isDraftDirty = signal(false);
  /** True once the PDF has been requested in this session — drives step sequencing only. */
  hasDownloaded = signal(false);

  /**
   * Set once the user has clicked "Open in Email App".
   *
   * `mailto:` gives no success or failure callback, so this does not mean the client opened —
   * only that we tried. It drives a "didn't open?" hint pointing at the Copy path, which is the
   * one that always works.
   */
  mailClientAttempted = signal(false);
  /** Org name + certificate number are fetched async; the draft seeds with placeholders first. */
  isPreparingDraft = signal(false);

  private orgName = signal<string>('');
  private certificateNumber = signal<string>(FAA_CERTIFICATE_PLACEHOLDER);

  /** Token guarding against a slow lookup for a previously-opened report landing late. */
  private prepareToken = 0;

  readonly otherRecipientId = OTHER_RECIPIENT;
  readonly stateReviewNote = STATE_REPORT_REVIEW_NOTE;

  // ============================================
  // LIFECYCLE
  // ============================================

  ngOnChanges(changes: SimpleChanges): void {
    const openChange = changes['isOpen'];
    const reportChange = changes['report'];

    const opened = !!openChange && openChange.currentValue && !openChange.previousValue;
    const closedNow = !!openChange && !openChange.currentValue && openChange.previousValue;
    const reportSwapped =
      !!reportChange && !reportChange.firstChange && reportChange.currentValue !== reportChange.previousValue;

    if (closedNow) {
      this.resetState();
      return;
    }

    // A different report is a different draft — a full reset, including the dirty flag.
    if (opened || (reportSwapped && this.isOpen)) {
      this.resetState();
      this.prepare();
    }

    if (opened) {
      this.focusDialog();
    }
  }

  /**
   * Move focus into the dialog once it has rendered, so keyboard users land inside the modal
   * rather than on whatever was behind the backdrop. Deferred a tick because the dialog is
   * behind an `*ngIf` and does not exist yet during ngOnChanges.
   */
  private focusDialog(): void {
    setTimeout(() => {
      const root = this.dialogRoot?.nativeElement;
      const first = root?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    });
  }

  // ============================================
  // STEP 1 — RECIPIENT
  // ============================================

  /**
   * Select a preset (or "Other recipient…").
   *
   * Regenerating here is a no-op today — the draft body does not mention the recipient — but the
   * dirty-flag path is asserted anyway so a future recipient-aware template cannot silently eat
   * someone's edits.
   */
  selectRecipient(id: string): void {
    if (this.isProcessing()) return;
    this.recipientChoice.set(id);
    this.regenerateDraftIfClean();
  }

  /** The selected preset, or `null` when "Other recipient…" is chosen. */
  selectedAgency(): ReportAgency | null {
    const id = this.recipientChoice();
    if (id === OTHER_RECIPIENT) return null;
    return this.agencyOptions().find((agency) => agency.id === id) ?? null;
  }

  /**
   * True when the address has to come from the free-text input: either "Other recipient…" is
   * chosen, or the selected preset's address is still pending confirmation.
   *
   * ⚠️ A pending preset renders as "address pending", never as an empty string dressed up as a
   * real address.
   */
  needsCustomRecipient(): boolean {
    const agency = this.selectedAgency();
    return !agency || isAgencyEmailPending(agency);
  }

  /** True when the selected preset exists but has no confirmed address yet. */
  selectedAgencyPending(): boolean {
    const agency = this.selectedAgency();
    return !!agency && isAgencyEmailPending(agency);
  }

  /** The address every downstream action uses. Empty string means "not chosen yet". */
  recipientAddress(): string {
    const agency = this.selectedAgency();
    if (agency && !isAgencyEmailPending(agency)) {
      return agency.email;
    }
    return (this.customEmail ?? '').trim();
  }

  hasValidRecipient(): boolean {
    return this.isValidEmail(this.recipientAddress());
  }

  /**
   * Validate email address format
   */
  isValidEmail(email: string): boolean {
    if (!email) return false;
    // Basic email validation regex
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  /**
   * Placeholder copy differs per report kind
   */
  get recipientPlaceholder(): string {
    return this.reportKind === 'state'
      ? 'e.g., pesticide.reporting@state.gov'
      : 'e.g., uas.reporting@faa.gov';
  }

  async copyRecipient(): Promise<void> {
    const address = this.recipientAddress();
    if (!address) return;
    await this.copyAndReport(address, 'Recipient address copied');
  }

  // ============================================
  // STEP 2 — DOWNLOAD
  // ============================================

  /**
   * Hand the download back to the host's existing flow. Marked as downloaded optimistically:
   * step sequencing is a visual hint, not a gate, so an over-eager mark is harmless.
   */
  requestDownload(): void {
    if (this.isProcessing()) return;
    this.hasDownloaded.set(true);
    this.downloadRequested.emit();
  }

  // ============================================
  // STEP 3 — THE DRAFT
  // ============================================

  /** Any keystroke in the subject or the body permanently pins the draft. */
  onDraftEdited(): void {
    this.isDraftDirty.set(true);
  }

  /**
   * The exact text Copy Message places on the clipboard: the BODY ONLY.
   *
   * It deliberately does NOT prepend "Subject: …". Every mail client wants the recipient,
   * subject and body in three separate fields, so a combined blob just has to be hand-split
   * before it can be pasted. Each part has its own copy action instead.
   */
  composedMessage(): string {
    return this.body;
  }

  async copySubject(): Promise<void> {
    if (!this.subject.trim()) return;
    await this.copyAndReport(this.subject, 'Subject copied');
  }

  /**
   * ⚠️ Reads the CURRENT field contents, never the originally generated draft.
   */
  async copyMessage(): Promise<void> {
    await this.copyAndReport(this.composedMessage(), 'Message copied');
  }

  /**
   * ⚠️ Also reads the CURRENT field contents.
   *
   * Built fresh on every call and on every change-detection pass, which is what makes the length
   * guard live — the body is editable and can be pasted into.
   */
  mailtoUrl(): string {
    return buildMailtoUrl({
      to: this.recipientAddress(),
      subject: this.subject,
      body: this.body
    });
  }

  /**
   * Whether the encoded URL is short enough to hand to the OS.
   *
   * Re-evaluated on every change-detection pass — a guard computed once at generation time would
   * be a defect, because the user can paste three paragraphs into the body afterwards.
   */
  isMailtoWithinLimit(): boolean {
    return isWithinMailtoLimit(this.mailtoUrl());
  }

  canOpenInEmailApp(): boolean {
    return !this.isProcessing() && this.hasValidRecipient() && this.isMailtoWithinLimit();
  }

  /**
   * The secondary path. It can silently fail (no registered mail client gives no callback), so
   * nothing in this flow depends on it working.
   */
  openInEmailApp(): void {
    if (!this.canOpenInEmailApp()) return;

    // An anchor click is handled more reliably for custom protocols than assigning
    // window.location.href, which some browsers ignore for mailto: inside a SPA.
    const link = document.createElement('a');
    link.href = this.mailtoUrl();
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // There is NO success signal for mailto: — a machine with no registered mail client simply
    // does nothing, silently. Rather than leave the user staring at an unchanged screen
    // wondering whether it worked, surface the fallback right where they're looking.
    this.mailClientAttempted.set(true);
  }

  // ============================================
  // FOOTER
  // ============================================

  canMarkSubmitted(): boolean {
    return this.hasValidRecipient() && !this.isProcessing();
  }

  /**
   * "Submitted" is the user's assertion, not a system fact — we never claim to have sent
   * anything. The host records the chosen recipient through the existing `/submit` endpoint.
   */
  markAsSubmitted(): void {
    if (this.isProcessing()) return;

    if (!this.hasValidRecipient()) {
      this.errorMessage.set('Enter or choose a valid recipient email address first.');
      return;
    }

    this.errorMessage.set(null);
    this.submitted.emit(this.recipientAddress());
  }

  /**
   * Close the modal. Locked out while a submission is in flight.
   */
  close(): void {
    if (this.isProcessing()) return;
    this.closed.emit();
  }

  // ============================================
  // HOST-DRIVEN FEEDBACK
  // ============================================

  /**
   * Set processing state (for async operations)
   */
  setProcessing(processing: boolean): void {
    this.isProcessing.set(processing);
  }

  /**
   * Surface an error inside the modal
   */
  setError(message: string | null): void {
    this.errorMessage.set(message);
  }

  // ============================================
  // A11Y / DISMISSAL
  // ============================================

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  /**
   * Escape closes; Tab cycles within the dialog so focus cannot wander behind the backdrop.
   */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) return;

    if (event.key === 'Escape' && !this.isProcessing()) {
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    const root = this.dialogRoot?.nativeElement;
    if (!root) return;

    const focusable = Array.from(
      root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter(
      (el) => el === document.activeElement || el.offsetWidth > 0 || el.offsetHeight > 0
    );

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (!active || !root.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // ============================================
  // INTERNALS
  // ============================================

  /**
   * Seed the modal for the currently open report.
   *
   * The draft is built twice on purpose: immediately with placeholders so the textarea is never
   * empty, then again once the org name and certificate number resolve. The second pass is
   * skipped if the user has started typing in the meantime.
   */
  private prepare(): void {
    const report = this.report;
    if (!report) return;

    const token = ++this.prepareToken;

    this.agencyOptions.set(this.resolveAgencyOptions());
    const preselected = preselectReportAgency(this.reportKind, report.state ?? null);
    this.recipientChoice.set(preselected ? preselected.id : OTHER_RECIPIENT);

    this.certificateNumber.set(
      this.reportKind === 'state' ? STATE_CERTIFICATE_PLACEHOLDER : FAA_CERTIFICATE_PLACEHOLDER
    );
    this.orgName.set('');
    this.regenerateDraftIfClean();

    const organizationId = this.organizationContext.currentOrganizationId();
    if (!organizationId) return;

    this.isPreparingDraft.set(true);
    void Promise.all([
      this.resolveOrganizationName(organizationId),
      // Never throws — a failed fetch resolves to the visible placeholder.
      resolveReportCertificateNumber(this.certificationService, {
        kind: this.reportKind,
        organizationId,
        state: report.state ?? null
      })
    ])
      .then(([name, certificateNumber]) => {
        if (token !== this.prepareToken) return; // a newer prepare() has taken over
        this.orgName.set(name);
        this.certificateNumber.set(certificateNumber);
        this.regenerateDraftIfClean();
      })
      .finally(() => {
        if (token === this.prepareToken) {
          this.isPreparingDraft.set(false);
        }
      });
  }

  /**
   * Which presets to offer.
   *
   * FAA reports see every FAA entry. State reports see ONLY entries whose jurisdiction matches
   * the report's state — offering an Arkansas agency for a Mississippi filing would be a footgun.
   * No match falls through to "Other recipient…", which always works.
   */
  private resolveAgencyOptions(): ReportAgency[] {
    const all = listReportAgencies(this.reportKind);

    if (this.reportKind !== 'state') {
      return all;
    }

    const jurisdiction = (this.report?.state ?? '').trim().toUpperCase();
    if (!jurisdiction) {
      return [];
    }

    return all.filter((agency) => agency.jurisdiction.toUpperCase() === jurisdiction);
  }

  /** Org name for the draft. Non-blocking: a failure leaves the builder's visible placeholder. */
  private async resolveOrganizationName(organizationId: string): Promise<string> {
    try {
      const org = await this.organizationService.getOrganization(organizationId);
      if (org?.name) return org.name;
    } catch {
      // fall through to the org-context lookup below
    }

    try {
      const mine = await this.organizationService.getMyOrganization();
      return mine?.name ?? '';
    } catch {
      return '';
    }
  }

  /** ⚠️ The single chokepoint that must never overwrite a user's typing. */
  private regenerateDraftIfClean(): void {
    if (this.isDraftDirty()) return;

    const draft = this.buildDraft();
    if (!draft) return;

    this.subject = draft.subject;
    this.body = draft.body;
  }

  private buildDraft(): { subject: string; body: string } | null {
    const report = this.report;
    if (!report) return null;

    const source = {
      state: report.state ?? '',
      startDate: report.startDate,
      endDate: report.endDate,
      options: (report.options ?? {}) as FaaReportOptions & StateReportOptions
    };

    return buildReportEmailTemplate({
      report: source,
      org: { name: this.orgName() },
      certificateNumber: this.certificateNumber(),
      kind: this.reportKind
    });
  }

  private async copyAndReport(text: string, successMessage: string): Promise<void> {
    const copied = await copyToClipboard(text);

    if (copied) {
      this.toast.success(successMessage);
    } else {
      // Never claim a copy that did not happen.
      this.toast.error('Couldn’t copy to the clipboard. Select the text and copy it manually.');
    }
  }

  /**
   * Clear form + feedback whenever the modal opens, closes, or swaps reports.
   */
  private resetState(): void {
    this.prepareToken++;
    this.customEmail = '';
    this.subject = '';
    this.body = '';
    this.recipientChoice.set(OTHER_RECIPIENT);
    this.agencyOptions.set([]);
    this.isDraftDirty.set(false);
    this.hasDownloaded.set(false);
    this.isPreparingDraft.set(false);
    this.errorMessage.set(null);
    this.isProcessing.set(false);
  }
}
